import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IoMdArrowBack } from "react-icons/io";
import {
  FiUsers,
  FiUser,
  FiClipboard,
  FiActivity,
  FiCalendar,
  FiChevronRight,
  FiClock,
  FiFileText,
  FiSearch,
} from "react-icons/fi";
import {
  MdPhone,
  MdMail,
  MdCake,
  MdLocationOn,
  MdRefresh,
} from "react-icons/md";
import moment from "moment";
import { getPartnerInfo } from "../../../../../Service/RM";
import Loader from "../../../../../reusableUI/loader";
import { showToast } from "../../../../../reusableUI/toaster";
import { capitalizeFirstLetter, formatPhoneNumber } from "../../../../../utils/Functions";
import { CommonImages } from "../../../../../reusableUI/images";

/* ----------------------------- status helpers ----------------------------- */
/* Adapted from partnerPortal/booking.js getDutyStatus + StatusBadge so this
   mobile view is self-contained (read-only). */
const getBookingStatus = (visit) => {
  if (!visit) return "Unknown";
  const statusKey = String(visit?.status || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

  if (statusKey === "endduty") return "Completed";
  if (statusKey === "completed") return "Completed";
  if (statusKey === "rejected") return "Cancelled";
  if (statusKey === "cancelled") return "Cancelled";
  if (statusKey === "expired") return "Expired";
  if (statusKey === "accepted") return "Confirmed";
  if (statusKey === "pending") return "Unassigned";
  if (statusKey === "notpaid") return "Voided";

  const today = moment().format("YYYY-MM-DD");
  const dutyInfo = Array.isArray(visit?.dutyInfo) ? visit.dutyInfo : [];
  const currentDuty = dutyInfo.find(
    (duty) => moment(duty?.date).format("YYYY-MM-DD") === today
  );
  if (currentDuty) {
    if (currentDuty.endDuty?.verified) return "Completed";
    if (currentDuty.startDuty?.verified) return "Duty Started";
  }

  if (statusKey === "paymentpending") return "Payment Pending";
  return "Unknown";
};

const STATUS_STYLE = {
  Unassigned: "text-red-600 bg-red-50",
  Confirmed: "text-green-600 bg-green-50",
  "Duty Started": "text-purple-600 bg-purple-50",
  Ongoing: "text-blue-600 bg-blue-50",
  Completed: "text-teal-600 bg-teal-50",
  Cancelled: "text-red-700 bg-red-50",
  Expired: "text-gray-500 bg-gray-100",
  "Payment Pending": "text-orange-600 bg-orange-50",
  Voided: "text-red-500 bg-red-50",
  Unknown: "text-gray-400 bg-gray-100",
};

const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-poppins font-medium shrink-0 ${
      STATUS_STYLE[status] || STATUS_STYLE.Unknown
    }`}
  >
    {status}
  </span>
);

const DUTY_STATUS_STYLE = (status) => {
  const s = String(status || "");
  if (s.includes("Completed")) return "text-teal-600 bg-teal-50";
  if (s.includes("Ongoing")) return "text-blue-600 bg-blue-50";
  if (s.includes("End Duty")) return "text-purple-600 bg-purple-50";
  if (s.includes("Yet to Start")) return "text-orange-600 bg-orange-50";
  if (s.includes("Payment pending")) return "text-orange-600 bg-orange-50";
  if (s.includes("Voided")) return "text-red-500 bg-red-50";
  return "text-gray-500 bg-gray-100";
};

const DutyStatusBadge = ({ status }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-poppins font-medium shrink-0 ${DUTY_STATUS_STYLE(
      status
    )}`}
  >
    {status}
  </span>
);

/* --------------------------- specialist helpers --------------------------- */
const getSpecialistName = (s) => {
  const info = s?.personalInfo?.basicInfo?.nameInfo || {};
  const title = info.title ? `${info.title}. ` : "";
  return `${title}${info.fullName || ""}`.trim() || "—";
};
const getSpecialistEmail = (s) =>
  s?.personalInfo?.basicInfo?.otherInfo?.email || "";
const getSpecialistDob = (s) =>
  s?.personalInfo?.basicInfo?.otherInfo?.dob || "";
const getSpecialistPhone = (s) => s?.phone || "";

const getProfileScore = (s) => {
  let count = 0;
  if (s?.personalInfo?.completed) count += 1;
  const emergencyCount = Array.isArray(s?.emergencyContactInfo)
    ? s.emergencyContactInfo.length
    : 0;
  if (emergencyCount === 2) count += 1;
  return count; // 0..2
};

const getRoleLabel = (subRole, role) => {
  if (subRole) {
    return capitalizeFirstLetter(
      subRole === "nursing-assistant" ? "Nursing Assistant" : subRole
    );
  }
  return role ? capitalizeFirstLetter(role) : "";
};

/* ----------------------------- booking helpers ---------------------------- */
const getPatientName = (b) =>
  b?.patient?.name ||
  b?.patientInfo?.personalInfo?.basicInfo?.nameInfo?.fullName ||
  "—";

const getServiceRole = (b) => {
  const label = getRoleLabel(b?.specialistInfo?.subRole, b?.specialistInfo?.role);
  return label || "—";
};

const momentFromField = (value) => {
  if (!value) return null;
  const raw = value?.$date || value;
  const m = moment(raw);
  return m.isValid() ? m : null;
};

const getDateRange = (b) => {
  const start = momentFromField(b?.visitInfo?.startDate);
  const end = momentFromField(b?.visitInfo?.endDate);
  if (start && end) {
    const s = start.format("DD MMM YYYY");
    const e = end.format("DD MMM YYYY");
    return s === e ? s : `${s} – ${e}`;
  }
  if (start) return start.format("DD MMM YYYY");
  if (end) return end.format("DD MMM YYYY");
  return "—";
};

const getDutyStaff = (b) =>
  b?.dutyStaff?.name || b?.specialistInfo?.name || "—";

const getPatientPhone = (visit) => {
  const relationship = String(
    visit?.patient?.relationship || visit?.patient?.relation || ""
  )
    .trim()
    .toLowerCase();

  if (relationship === "self") {
    return (
      visit?.patientInfo?.phone ||
      visit?.patient?.phone ||
      visit?.patientInfo?.personalInfo?.basicInfo?.otherInfo?.phone ||
      ""
    );
  }

  return (
    visit?.patientInfo?.personalInfo?.basicInfo?.otherInfo?.phone ||
    visit?.patientInfo?.phone ||
    visit?.patient?.phone ||
    ""
  );
};

const getPatientGender = (visit) =>
  visit?.patient?.gender ||
  visit?.patientInfo?.personalInfo?.basicInfo?.otherInfo?.gender ||
  "";

const getPatientDob = (visit) =>
  visit?.patientInfo?.personalInfo?.basicInfo?.otherInfo?.dob ||
  visit?.patient?.dob ||
  "";

const getPatientAge = (visit) => {
  const dob = momentFromField(getPatientDob(visit));
  return dob ? `${moment().diff(dob, "years")} yrs` : "—";
};

const getVisitAddressInfo = (visit) =>
  visit?.visitInfo?.addressInfo ||
  visit?.patientInfo?.personalInfo?.addressInfo ||
  {};

const getFullAddress = (visit) => {
  const address = getVisitAddressInfo(visit);
  return [
    address?.address1,
    address?.address2,
    address?.district,
    address?.city,
    address?.state,
    address?.pincode,
  ]
    .filter(Boolean)
    .join(", ");
};

const getRequestedServices = (visit) => {
  const services = Array.isArray(visit?.visitInfo?.services)
    ? visit.visitInfo.services
    : [];
  const labels = services
    .map((item) =>
      typeof item === "string"
        ? item
        : item?.service || item?.name || item?.label || ""
    )
    .filter(Boolean);
  return labels.join(", ") || visit?.visitInfo?.complaints || "—";
};

const getDutyType = (visit) => {
  const shift = visit?.visitInfo?.shiftType;
  if (shift) return capitalizeFirstLetter(shift);
  const dutyType = String(visit?.dutyType || "").toLowerCase();
  if (dutyType === "onetime") return "One Time";
  return dutyType ? capitalizeFirstLetter(dutyType) : "—";
};

const getStandbyName = (visit, position) => {
  if (position === 1) {
    return (
      visit?.standBySpecialist?.name ||
      visit?.standBySpecialists?.specialists1Name ||
      "N/A"
    );
  }
  return (
    visit?.backupSpecialist?.name ||
    visit?.standBySpecialists?.specialists2Name ||
    "N/A"
  );
};

const getPaymentType = (visit) => {
  const mode = String(visit?.paymentMode || "").trim().toLowerCase();
  if (mode === "daily_qr") return "Daily Payment";
  if (mode === "location_qr") return "Upfront (QR)";
  if (mode === "online") return "Upfront (Online)";
  return "Upfront Payment";
};

const getPaymentTotal = (visit) => {
  const value =
    visit?.paymentInfo?.totalFees ??
    visit?.paymentInfo?.grandTotal ??
    visit?.visitInfo?.fees;
  const number = Number(value);
  return Number.isFinite(number) ? `Rs. ${number.toFixed(2)}` : "—";
};

const getDischargeSummaries = (visit) => {
  const documents = visit?.visitInfo?.dischargeSummaryDoc;
  if (Array.isArray(documents)) return documents.filter(Boolean);
  return documents && typeof documents === "object" ? [documents] : [];
};

const getSearchText = (visit) =>
  [
    getPatientName(visit),
    getPatientPhone(visit),
    visit?.jobID,
    getVisitAddressInfo(visit)?.city,
    getServiceRole(visit),
    getDutyStaff(visit),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const isActiveBooking = (b) => {
  const s = String(b?.status || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  return !["cancelled", "expired", "rejected", "notpaid", "voided"].includes(s);
};

const hasDuties = (b) =>
  Array.isArray(b?.dutyInfo) && b.dutyInfo.length > 0;

/* ------------------------------- duty helpers ------------------------------ */
/* Ported from common/visits.js so the mobile Duty Details view matches the
   desktop panel (read-only). */
const normalizeId = (id) => {
  if (!id) return "";
  if (typeof id === "string") return id;
  if (typeof id === "object" && id.$oid) return String(id.$oid);
  return String(id);
};

const getAssessments = (visit) => (Array.isArray(visit?.assessment) ? visit.assessment : []);

const getActiveAssessment = (visit) => {
  const arr = getAssessments(visit);
  return arr.find((a) => a?.active) || arr[0] || null;
};

const getTreatmentPlanStatus = (visit) => {
  const assessment = getActiveAssessment(visit);
  return String(assessment?.treatmentPlan?.status || "").trim().toLowerCase();
};

const hasFurtherTreatmentRequested = (visit) => {
  const tpStatus = getTreatmentPlanStatus(visit);
  return tpStatus === "pending" || tpStatus === "requested";
};

const getFurtherTreatmentLabel = (visit) =>
  hasFurtherTreatmentRequested(visit) ? "Further Treatment Requested" : "";

const isRequestsVisit = (visit) => {
  const st = String(visit?.status || "").trim().toLowerCase();
  return st === "pending" || st === "payment pending" || st === "not paid";
};

const getDutyStaffNameFromVisit = (visit) =>
  visit?.dutyStaff?.name || visit?.specialistInfo?.name || visit?.assessmentStaff?.name || "N/A";

const buildVirtualDutiesFromVisitInfo = (visit) => {
  const startRaw = visit?.visitInfo?.startDate;
  const slot = String(visit?.visitInfo?.slot || "").trim();
  if (!startRaw || !slot) return [];

  const start = moment(startRaw).local().startOf("day");
  if (!start.isValid()) return [];

  const end = visit?.visitInfo?.endDate
    ? moment(visit.visitInfo.endDate).local().startOf("day")
    : null;

  const daysRaw = Number(visit?.visitInfo?.days);
  let totalDays = Number.isFinite(daysRaw) && daysRaw > 0 ? Math.floor(daysRaw) : 0;
  if (!totalDays && end?.isValid()) totalDays = end.diff(start, "days") + 1;
  if (!totalDays || totalDays <= 0) totalDays = 1;

  const staffName = getDutyStaffNameFromVisit(visit);
  const rows = [];
  for (let i = 0; i < totalDays; i++) {
    const d = start.clone().add(i, "day");
    if (end?.isValid() && d.isAfter(end, "day")) break;

    rows.push({
      _id: `virtual-${normalizeId(visit?._id) || "visit"}-${d.format("YYYY-MM-DD")}`,
      date: d.format("YYYY-MM-DD"),
      day: i + 1,
      slot,
      assignedStaffName: staffName,
      attendedBy: {
        role: visit?.specialistInfo?.role,
        userID: { $oid: normalizeId(visit?.dutyStaff?.userID || visit?.specialistInfo?.userID) },
      },
      startDuty: {},
      endDuty: {},
      liveStatus: "",
      payout: null,
      services: [],
    });
  }
  return rows;
};

const getDisplayDuties = (visit) => {
  if (!visit) return [];
  const st = String(visit?.status || "").trim().toLowerCase();
  const dutyInfo = Array.isArray(visit?.dutyInfo) ? visit.dutyInfo : [];

  if (st === "payment pending" || st === "not paid") {
    const startRaw = visit?.visitInfo?.startDate?.$date || visit?.visitInfo?.startDate;
    const endRaw = visit?.visitInfo?.endDate?.$date || visit?.visitInfo?.endDate;
    const slot = String(visit?.visitInfo?.slot || "").trim();

    let dateStr = "";
    if (startRaw && endRaw) {
      const s = moment(startRaw).local().format("DD/MM/YYYY");
      const e = moment(endRaw).local().format("DD/MM/YYYY");
      dateStr = s === e ? s : `${s} - ${e}`;
    } else if (startRaw) {
      dateStr = moment(startRaw).local().format("DD/MM/YYYY");
    } else if (endRaw) {
      dateStr = moment(endRaw).local().format("DD/MM/YYYY");
    } else {
      dateStr = "-";
    }

    const staffName = getDutyStaffNameFromVisit(visit);
    return [{
      _id: `virtual-${normalizeId(visit?._id) || "visit"}-range`,
      date: startRaw || endRaw || null,
      displayDateRange: dateStr,
      slot,
      assignedStaffName: staffName,
      attendedBy: {
        role: visit?.specialistInfo?.role,
        userID: { $oid: normalizeId(visit?.dutyStaff?.userID || visit?.specialistInfo?.userID) },
      },
      startDuty: {},
      endDuty: {},
      liveStatus: "",
      payout: null,
      services: [],
    }];
  }

  if (dutyInfo.length > 0) return dutyInfo;
  if (isRequestsVisit(visit)) return buildVirtualDutiesFromVisitInfo(visit);
  return [];
};

const isDutyStarted = (duty) => !!duty?.startDuty?.verified;
const isDutyEnded = (duty) => !!duty?.endDuty?.verified;

const getVisitCardStatus = (visit) => {
  if (!visit) return "Unknown";

  const statusKey = String(visit?.status || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

  // An ended duty completes only that duty. The overall visit is complete only
  // after the backend moves the parent visit to the explicit completed status.
  if (statusKey === "completed") return "Completed";
  if (statusKey === "rejected" || statusKey === "cancelled") return "Cancelled";
  if (statusKey === "expired") return "Expired";
  if (statusKey === "paymentpending") return "Payment Pending";
  if (statusKey === "notpaid") return "Voided";
  if (statusKey === "startduty" || statusKey === "endduty") return "Ongoing";

  const duties = getDisplayDuties(visit);
  if (duties.some((duty) => isDutyStarted(duty) || isDutyEnded(duty))) {
    return "Ongoing";
  }

  if (statusKey === "accepted") return "Confirmed";
  if (statusKey === "pending") return "Unassigned";
  return "Unknown";
};

const getDutyStatusLabel = (visit, duty) => {
  const st = String(visit?.status || "").trim().toLowerCase();
  if (st === "not paid") return "Voided";
  if (st === "payment pending") return "Payment pending";

  const further = getFurtherTreatmentLabel(visit);

  if (st === "completed" || st.includes("completed")) {
    return further ? `Completed - ${further}` : "Completed";
  }

  if (!isDutyStarted(duty) && !isDutyEnded(duty)) return "Yet to Start";

  if (isDutyStarted(duty) && !isDutyEnded(duty)) {
    return further ? `Ongoing - ${further}` : "Ongoing";
  }

  if (isDutyEnded(duty)) {
    return further ? `End Duty - ${further}` : "End Duty";
  }

  return "Yet to Start";
};

const formatCheckTime = (t) => (t ? moment(t).format("hh:mm A") : "-");

const formatDutyTimeFromStartEnd = (duty, visit) => {
  const st = duty?.startTime?.$date || duty?.startTime;
  const et = duty?.endTime?.$date || duty?.endTime;
  const isOneTime = String(visit?.dutyType || "").trim().toLowerCase() === "onetime";

  if (!st && !et) return "";

  if (isOneTime) {
    const timeToUse = st || et;
    return timeToUse ? moment(timeToUse).local().format("hh:mm A") : "";
  }

  if (st && !et) return moment(st).local().format("hh:mm A");
  if (!st && et) return moment(et).local().format("hh:mm A");

  const startLocal = moment(st).local().format("hh:mm A");
  const endLocal = moment(et).local().format("hh:mm A");
  if (startLocal === endLocal) return startLocal;
  return `${startLocal} to ${endLocal}`;
};

const getDutyTimeLabel = (visit, duty) => {
  const byStartEnd = formatDutyTimeFromStartEnd(duty, visit);
  if (byStartEnd) return byStartEnd;
  if (duty?.slot) return duty.slot;
  if (visit?.visitInfo?.slot) return visit.visitInfo.slot;
  const tpLabel = getActiveAssessment(visit)?.treatmentPlan?.timings?.label;
  if (tpLabel) return tpLabel;
  return "-";
};

const formatAmount = (n) => {
  if (n == null || n === "") return "";
  const num = Number(n);
  if (!Number.isFinite(num)) return "";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(num);
};

const getPayoutText = (visit, duty, started) => {
  const isDailyQr =
    String(visit?.paymentMode || "").trim().toLowerCase() === "daily_qr";
  const paymentStatus = String(duty?.payment?.status || "").trim().toLowerCase();
  if (isDailyQr && paymentStatus !== "paid") return "-";
  if (!started && !isDailyQr) return "-";

  const amount =
    duty?.payout?.payoutToTheVendor?.amountToTransfer ??
    duty?.payout?.payoutToTheVendor?.netPayout ??
    duty?.payout?.fees ??
    null;
  const status = duty?.payout?.payoutToTheVendor?.status
    ? String(duty.payout.payoutToTheVendor.status).toLowerCase()
    : "";
  const amtStr = formatAmount(amount);

  if (!amtStr && !status) return "-";
  if (amtStr && status) return `Rs. ${amtStr} (${status})`;
  if (amtStr) return `Rs. ${amtStr}`;
  return `(${status})`;
};

const getDutyHcpName = (visit, duty) => {
  if (duty?.payouts?.attendedBy) return duty.payouts.attendedBy;

  const nameInfo = duty?.attendedBy?.userID?.personalInfo?.basicInfo?.nameInfo;
  if (nameInfo?.fullName) {
    return `${nameInfo?.title ? nameInfo.title + " " : ""}${nameInfo.fullName}`.trim();
  }

  if (typeof duty?.attendedBy === "string" && duty.attendedBy.trim()) {
    return duty.attendedBy.trim();
  }
  if (duty?.attendedBy?.name) return duty.attendedBy.name;

  const dutyLevelName =
    duty?.specialistInfo?.name ||
    duty?.dutyStaff?.name ||
    duty?.actingHCP?.name ||
    duty?.actingHcpInfo?.name ||
    duty?.assignedStaff?.name ||
    duty?.assignedStaffName;

  if (dutyLevelName) return dutyLevelName;

  return visit?.dutyStaff?.name || visit?.specialistInfo?.name || "N/A";
};

/* ------------------------------- UI pieces -------------------------------- */
const Field = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-1.5 text-[12px] font-poppins text-gray-600 min-w-0">
    {Icon && <Icon size={13} className="text-gray-400 shrink-0" />}
    <span className="truncate">{children}</span>
  </div>
);

const SectionEmpty = ({ text }) => (
  <p className="text-center text-[12px] font-poppins text-gray-400 py-10">
    {text || "No records"}
  </p>
);

const Card = ({ children }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-xl p-3.5 shadow-sm">
    {children}
  </div>
);

const groupDuties = (duties) => {
  const today = moment().startOf("day");
  const groups = { today: [], upcoming: [], past: [] };
  (Array.isArray(duties) ? duties : []).forEach((d) => {
    if (!d?.date) {
      groups.upcoming.push(d);
      return;
    }
    const dm = moment(d.date).startOf("day");
    if (dm.isSame(today)) groups.today.push(d);
    else if (dm.isAfter(today)) groups.upcoming.push(d);
    else groups.past.push(d);
  });
  return groups;
};

const SummaryItem = ({ label, value }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-poppins text-gray-400 uppercase tracking-wide">{label}</p>
    <p className="text-[13px] font-poppins font-medium text-[#1F2937] truncate">{value || "-"}</p>
  </div>
);

const DetailSection = ({ title, icon: Icon, children }) => (
  <section className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-sm overflow-hidden">
    <div className="flex items-center gap-2 px-4 py-3 border-b border-[#EEF0F4]">
      {Icon ? <Icon size={15} className="text-[#1D4ED8] shrink-0" /> : null}
      <h3 className="font-poppins text-[13px] font-semibold text-[#1F2937]">
        {title}
      </h3>
    </div>
    <div className="px-4 py-3">{children}</div>
  </section>
);

const DetailRow = ({ label, value, children }) => (
  <div className="grid grid-cols-[112px_minmax(0,1fr)] gap-3 py-2 border-b border-[#F0F2F5] last:border-b-0">
    <span className="font-poppins text-[11px] text-gray-500">{label}</span>
    <span className="font-poppins text-[12px] font-medium text-[#1F2937] break-words text-right">
      {children || value || "—"}
    </span>
  </div>
);

const FilterToolbar = ({ value, onChange, filter, onFilterChange, options }) => (
  <div className="space-y-2 pb-1">
    <div className="relative">
      <FiSearch
        size={16}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
      />
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Search patient, phone, ID or location"
        className="w-full rounded-xl border border-[#E5E7EB] bg-white py-2.5 pl-9 pr-3 font-poppins text-[12px] text-[#1F2937] outline-none focus:border-[#1D4ED8]"
      />
    </div>
    <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {options.map((option) => {
        const selected = filter === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onFilterChange(option.value)}
            className={`shrink-0 rounded-full border px-3 py-1.5 font-poppins text-[11px] font-medium transition-colors ${
              selected
                ? "border-[#1D4ED8] bg-[#1D4ED8] text-white"
                : "border-[#DCE2EC] bg-white text-[#5F6675]"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  </div>
);

const PatientHistorySummary = ({ visit }) => {
  const groups = [
    ["Pre-existing Conditions", visit?.patientInfo?.preExistingConditions?.data],
    ["Allergies", visit?.patientInfo?.allergies?.data],
    ["Medical History", visit?.patientInfo?.medicalHistory?.data],
    ["Family Medical History", visit?.patientInfo?.familyHistory?.data],
  ].filter(([, entries]) => Array.isArray(entries) && entries.length > 0);

  if (groups.length === 0) {
    return <p className="font-poppins text-[12px] italic text-gray-400">No information to display</p>;
  }

  return (
    <div className="space-y-3">
      {groups.map(([label, entries]) => (
        <div key={label}>
          <p className="font-poppins text-[11px] font-semibold text-gray-500 mb-1">
            {label}
          </p>
          <div className="space-y-1">
            {entries.map((entry, index) => (
              <p
                key={entry?._id || `${label}-${index}`}
                className="font-poppins text-[12px] text-[#1F2937]"
              >
                {typeof entry === "string"
                  ? entry
                  : entry?.medicalCondition ||
                  entry?.condition ||
                  entry?.allergy ||
                  entry?.name ||
                  entry?.title ||
                  "Recorded information"}
              </p>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const DutyRow = ({ visit, duty, total }) => {
  const started = !!duty?.startDuty?.verified;
  const displayTime = getDutyTimeLabel(visit, duty);
  const status = getDutyStatusLabel(visit, duty);
  const staff = getDutyHcpName(visit, duty);
  const dateLabel = duty?.displayDateRange
    ? duty.displayDateRange
    : duty?.date
      ? moment(duty.date).format("DD/MM/YYYY")
      : "-";
  const checkIn = duty?.startDuty?.verified ? formatCheckTime(duty?.startDuty?.time) : "-";
  const checkOut = duty?.endDuty?.verified ? formatCheckTime(duty?.endDuty?.time) : "-";
  const payout = getPayoutText(visit, duty, started);

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-3.5 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-poppins font-semibold text-[14px] text-[#1F2937] truncate">
            {dateLabel}
          </p>
          <p className="text-[11px] font-poppins text-gray-500 truncate">
            {displayTime && displayTime !== "-" ? displayTime : ""}
            {duty?.day != null && total > 1 ? ` · Day ${duty.day}/${total}` : ""}
          </p>
        </div>
        <DutyStatusBadge status={status} />
      </div>

      <div className="mt-3 space-y-2">
        <Field icon={FiUser}>
          <span className="text-gray-800 font-medium">{staff}</span>
        </Field>

        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg bg-[#F5F7FB] px-2.5 py-1.5">
            <p className="text-[10px] font-poppins text-gray-400">Check-in</p>
            <p className="text-[13px] font-poppins text-gray-800 font-semibold">{checkIn}</p>
          </div>
          <div className="rounded-lg bg-[#F5F7FB] px-2.5 py-1.5">
            <p className="text-[10px] font-poppins text-gray-400">Check-out</p>
            <p className="text-[13px] font-poppins text-gray-800 font-semibold">{checkOut}</p>
          </div>
        </div>

        <div className="flex items-center justify-between text-[12px] font-poppins">
          <span className="text-gray-500">Payout</span>
          <span className="text-gray-800 font-semibold">{payout}</span>
        </div>
      </div>
    </div>
  );
};

const DutyDetails = ({ visit, duties, onClose, mode = "visits" }) => {
  const grouped = groupDuties(duties);
  const addressInfo = getVisitAddressInfo(visit);
  const fullAddress = getFullAddress(visit);
  const phone = getPatientPhone(visit);
  const dischargeSummaries = getDischargeSummaries(visit);
  const timeline = Array.isArray(visit?.timeline) ? visit.timeline : [];
  const completedAssessments = Array.isArray(visit?.assessment)
    ? visit.assessment.filter((assessment) => assessment?.completed)
    : [];
  const reportDuties = duties.filter(
    (duty) => Array.isArray(duty?.services) && duty.services.length > 0
  );
  const mapUrl =
    addressInfo?.coordinates?.lat && addressInfo?.coordinates?.lng
      ? `https://www.google.com/maps/search/?api=1&query=${addressInfo.coordinates.lat},${addressInfo.coordinates.lng}`
      : "";
  const sections = [
    { key: "today", label: "Today", duties: grouped.today },
    { key: "upcoming", label: "Upcoming", duties: grouped.upcoming },
    { key: "past", label: "Past", duties: grouped.past },
  ].filter((s) => s.duties.length > 0);

  return (
    <div className="fixed inset-0 z-50 bg-[#F5F7FB] flex flex-col">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-3 py-3 flex items-center gap-2">
        <button
          type="button"
          onClick={onClose}
          className="p-2 -ml-1 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Back"
        >
          <IoMdArrowBack size={22} color="#111827" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-poppins font-medium text-[#1D4ED8]">
            {mode === "bookings" ? "Booking details" : "Visit details"}
          </p>
          <p className="font-poppins font-semibold text-[15px] text-[#1F2937] truncate">
            {getPatientName(visit)}
          </p>
          <p className="text-[11px] font-poppins text-gray-400 truncate">
            {getDateRange(visit)}
            {visit?.visitInfo?.slot ? ` · ${visit.visitInfo.slot}` : ""}
          </p>
        </div>
        <StatusBadge
          status={mode === "visits" ? getVisitCardStatus(visit) : getBookingStatus(visit)}
        />
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-3 space-y-4">
        {/* Summary */}
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-sm p-4 grid grid-cols-2 gap-3">
          <SummaryItem label="Service" value={getServiceRole(visit)} />
          <SummaryItem
            label="Location"
            value={capitalizeFirstLetter(visit?.visitInfo?.addressInfo?.city)}
          />
          <SummaryItem label="Assigned Staff" value={getDutyStaff(visit)} />
          <SummaryItem label="Total Duties" value={String(duties.length)} />
        </div>

        {duties.length === 0 ? (
          <SectionEmpty text="No duty records available" />
        ) : (
          sections.map((section) => (
            <div key={section.key}>
              <p className="text-[12px] font-poppins font-semibold text-gray-500 px-1 mb-2">
                {section.label} ({section.duties.length})
              </p>
              <div className="space-y-2">
                {section.duties.map((duty, idx) => (
                  <DutyRow
                    key={normalizeId(duty?._id) || `${section.key}-${idx}`}
                    visit={visit}
                    duty={duty}
                    total={duties.length}
                  />
                ))}
              </div>
            </div>
          ))
        )}

        <DetailSection title="Booking information" icon={FiClipboard}>
          <DetailRow label="Booking ID" value={visit?.jobID} />
          <DetailRow label="Total days" value={visit?.visitInfo?.days} />
          <DetailRow label="Duty type" value={getDutyType(visit)} />
          <DetailRow label="Requested services" value={getRequestedServices(visit)} />
        </DetailSection>

        <DetailSection title="Assignment" icon={FiUsers}>
          <DetailRow label="Service date" value={getDateRange(visit)} />
          <DetailRow label="Time" value={visit?.visitInfo?.slot} />
          <DetailRow label="Duty staff" value={getDutyStaff(visit)} />
          <DetailRow label="Standby staff 1" value={getStandbyName(visit, 1)} />
          <DetailRow label="Standby staff 2" value={getStandbyName(visit, 2)} />
        </DetailSection>

        <DetailSection title="Patient & contact" icon={FiUser}>
          <DetailRow label="Gender" value={capitalizeFirstLetter(getPatientGender(visit))} />
          <DetailRow label="Age" value={getPatientAge(visit)} />
          <DetailRow label="Phone">
            {phone ? (
              <a href={`tel:${phone}`} className="text-[#1D4ED8] underline">
                {formatPhoneNumber(Number(phone))}
              </a>
            ) : (
              "—"
            )}
          </DetailRow>
          <DetailRow label="Address">
            {mapUrl ? (
              <a
                href={mapUrl}
                target="_blank"
                rel="noreferrer"
                className="text-[#1D4ED8] underline"
              >
                {fullAddress || "View location"}
              </a>
            ) : (
              fullAddress || "—"
            )}
          </DetailRow>
        </DetailSection>

        <DetailSection title="Payment" icon={FiFileText}>
          <DetailRow label="Payment type" value={getPaymentType(visit)} />
          <DetailRow
            label="Payment status"
            value={visit?.paymentInfo?.paidInfo?.paid ? "Paid" : getBookingStatus(visit)}
          />
          {Array.isArray(visit?.paymentInfo?.serviceFees)
            ? visit.paymentInfo.serviceFees.map((item, index) => {
                const amount = Number(item?.fee ?? item?.fees);
                return (
                  <DetailRow
                    key={item?._id || index}
                    label={item?.service || item?.name || `Service ${index + 1}`}
                    value={Number.isFinite(amount) ? `Rs. ${amount.toFixed(2)}` : "—"}
                  />
                );
              })
            : null}
          <DetailRow label="Total" value={getPaymentTotal(visit)} />
        </DetailSection>

        <DetailSection title="Clinical reports" icon={FiFileText}>
          {completedAssessments.length === 0 && reportDuties.length === 0 ? (
            <p className="font-poppins text-[12px] italic text-gray-400">
              No reports available yet
            </p>
          ) : (
            <div className="space-y-3">
              {completedAssessments.map((assessment, index) => (
                <div
                  key={assessment?._id || `assessment-${index}`}
                  className="rounded-lg bg-[#F5F7FB] px-3 py-2.5"
                >
                  <p className="font-poppins text-[12px] font-semibold text-[#1F2937]">
                    Physiotherapy Assessment {completedAssessments.length > 1 ? index + 1 : ""}
                  </p>
                  <p className="mt-1 font-poppins text-[11px] text-gray-600">
                    {assessment?.treatmentPlan?.targetArea ||
                      assessment?.onObservation ||
                      "Assessment completed"}
                  </p>
                </div>
              ))}
              {reportDuties.map((duty, index) => (
                <div
                  key={normalizeId(duty?._id) || `report-duty-${index}`}
                  className="rounded-lg border border-[#E5E7EB] px-3 py-2.5"
                >
                  <p className="font-poppins text-[11px] font-semibold text-gray-500">
                    {duty?.date ? moment(duty.date).format("DD MMM YYYY") : `Duty ${index + 1}`}
                  </p>
                  <div className="mt-1 space-y-1">
                    {duty.services.map((service, serviceIndex) => (
                      <p
                        key={service?._id || serviceIndex}
                        className="font-poppins text-[12px] text-[#1F2937]"
                      >
                        {service?.serviceName ||
                          service?.name ||
                          service?.title ||
                          service?.service ||
                          `Service ${serviceIndex + 1}`}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DetailSection>

        <DetailSection title="Discharge summaries" icon={FiFileText}>
          {dischargeSummaries.length === 0 ? (
            <p className="font-poppins text-[12px] italic text-gray-400">
              No discharge summaries uploaded
            </p>
          ) : (
            <div className="space-y-2">
              {dischargeSummaries.map((document, index) => {
                const url = document?.url || document?.fileUrl || document?.documentUrl;
                return (
                  <div
                    key={document?._id || index}
                    className="flex items-center justify-between gap-3 rounded-lg bg-[#F5F7FB] px-3 py-2.5"
                  >
                    <span className="min-w-0 truncate font-poppins text-[12px] text-[#1F2937]">
                      {document?.title || `Discharge Summary ${index + 1}`}
                    </span>
                    {url ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0 font-poppins text-[11px] font-semibold text-[#1D4ED8]"
                      >
                        View
                      </a>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </DetailSection>

        <DetailSection title="Patient history" icon={FiFileText}>
          <PatientHistorySummary visit={visit} />
        </DetailSection>

        <DetailSection title="Timeline" icon={FiClock}>
          {timeline.length === 0 ? (
            <p className="font-poppins text-[12px] italic text-gray-400">
              No timeline events recorded
            </p>
          ) : (
            <div className="relative ml-1 border-l border-blue-200 pl-4 space-y-4">
              {timeline.map((item, index) => (
                <div key={item?._id || index} className="relative">
                  <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full border-2 border-[#1D4ED8] bg-white" />
                  <p className="font-poppins text-[11px] text-gray-400">
                    {item?.dateTime ? moment(item.dateTime).format("DD MMM YYYY, hh:mm A") : "—"}
                  </p>
                  <p className="font-poppins text-[12px] font-semibold text-[#1F2937]">
                    {item?.event || "Update"}
                  </p>
                  {item?.description ? (
                    <p className="mt-0.5 whitespace-pre-line font-poppins text-[11px] leading-relaxed text-gray-600">
                      {item.description}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </DetailSection>
      </div>
    </div>
  );
};

const MOBILE_TABS = new Set(["careTeam", "patients", "bookings", "visits"]);
const BOOKING_FILTERS = [
  { value: "all", label: "All" },
  { value: "payment", label: "Payment pending" },
  { value: "confirmed", label: "Confirmed" },
  { value: "completed", label: "Completed" },
  { value: "others", label: "Others" },
];
const VISIT_FILTERS = [
  { value: "all", label: "All" },
  { value: "upcoming", label: "Upcoming" },
  { value: "ongoing", label: "Ongoing" },
  { value: "payment", label: "Payment pending" },
  { value: "completed", label: "Completed" },
];

const VISIT_MONTH_CATEGORY_LABELS = {
  today: "Today's Appointment List",
  tomorrow: "Tomorrow's Appointment List",
  week: "Week's Appointment List",
  month: "Month's Appointment List",
  completed: "Completed Appointment List",
};

const getVisitStatusKey = (visit) =>
  String(visit?.status || "")
    .trim()
    .toLowerCase();

/* Ported from common/visits.js matchesFilter so mobile "All" defaults to
   Payment pending + accepted + startDuty + endDuty rows (not completed/others). */
const matchesVisitDutyFilter = (visit, duty, filter) => {
  const isStarted = isDutyStarted(duty);
  const isEnded = isDutyEnded(duty);
  const isToday = duty?.date ? moment(duty.date).isSame(moment(), "day") : false;
  const visitStatus = getVisitStatusKey(visit);

  if (visitStatus === "pending") return false;

  if (filter === "all") {
    if (
      ["expired", "cancelled", "not paid", "not available", "rejected"].includes(
        visitStatus
      )
    ) {
      return false;
    }
    return (
      matchesVisitDutyFilter(visit, duty, "ongoing") ||
      matchesVisitDutyFilter(visit, duty, "upcoming") ||
      matchesVisitDutyFilter(visit, duty, "payment")
    );
  }

  if (filter === "upcoming") {
    if (
      ["expired", "cancelled", "not paid", "not available", "rejected"].includes(
        visitStatus
      )
    ) {
      return false;
    }
    const isVisitOngoing = ["ongoing", "startduty", "endduty"].some((s) =>
      visitStatus.includes(s)
    );
    return (
      !isStarted &&
      !isEnded &&
      (!isToday || visitStatus === "accepted" || isVisitOngoing)
    );
  }

  if (filter === "ongoing") {
    if (
      [
        "expired",
        "cancelled",
        "not paid",
        "not available",
        "rejected",
        "completed",
      ].includes(visitStatus)
    ) {
      return false;
    }
    const isOngoingDuty = (isStarted && !isEnded) || isEnded;
    const isVisitOngoing = ["ongoing", "startduty", "endduty"].some((s) =>
      visitStatus.includes(s)
    );
    return isOngoingDuty || (!isStarted && !isEnded && isVisitOngoing);
  }

  if (filter === "payment") {
    return visitStatus === "payment pending";
  }

  if (filter === "completed") {
    return visitStatus === "completed" || visitStatus.includes("completed");
  }

  return false;
};

const getDutyMonthCategory = (dutyDate, visitStatus) => {
  if (visitStatus === "completed" || visitStatus.includes("completed")) {
    return "completed";
  }
  if (!dutyDate) return "month";

  const mDate = moment(dutyDate).startOf("day");
  const today = moment().startOf("day");
  const tomorrow = moment().add(1, "day").startOf("day");
  const endOfWeek = moment().add(7, "days").endOf("day");

  if (mDate.isSame(today, "day")) return "today";
  if (mDate.isSame(tomorrow, "day")) return "tomorrow";
  if (mDate.isAfter(tomorrow) && mDate.isSameOrBefore(endOfWeek)) return "week";
  return "month";
};

const matchesBookingFilter = (visit, filter) => {
  if (filter === "all") return true;
  const status = getBookingStatus(visit);
  if (filter === "payment") return status === "Payment Pending";
  if (filter === "confirmed") return status === "Confirmed";
  if (filter === "completed") return status === "Completed";
  return ["Cancelled", "Expired", "Voided", "Unknown"].includes(status);
};

/* -------------------------------- component ------------------------------- */
const PartnerViewMobile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routeParams = new URLSearchParams(location.search);
  const userID = location.state?.userID || routeParams.get("partnerId");
  const requestedTab = routeParams.get("tab");

  const [loading, setLoading] = useState(true);
  const [partner, setPartner] = useState(null);
  const [careTeam, setCareTeam] = useState([]);
  const [patients, setPatients] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [activeTab, setActiveTab] = useState(() =>
    MOBILE_TABS.has(requestedTab) ? requestedTab : "careTeam"
  );
  const [recordSearch, setRecordSearch] = useState("");
  const [bookingFilter, setBookingFilter] = useState("all");
  const [visitFilter, setVisitFilter] = useState("all");
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    const tabFromUrl = new URLSearchParams(location.search).get("tab");
    if (MOBILE_TABS.has(tabFromUrl)) setActiveTab(tabFromUrl);
  }, [location.search]);

  useEffect(() => {
    if (!userID) {
      setLoading(false);
      showToast("No partner selected", "Error");
      return;
    }

    let cancelled = false;

    const fetchPartner = async () => {
      setLoading(true);
      try {
        const result = await getPartnerInfo(userID);
        if (cancelled) return;

        const partnerData =
          result?.data?.patrnerInfo ||
          result?.data?.partnerInfo ||
          result?.patrnerInfo ||
          result?.data ||
          null;
        const specialists =
          result?.data?.specialists || result?.specialists || [];
        const bookingList =
          result?.data?.bookings || result?.bookings || [];

        setPartner(partnerData);
        setCareTeam(specialists.filter((s) => s?.role !== "patient"));
        setPatients(specialists.filter((s) => s?.role === "patient"));
        setBookings(Array.isArray(bookingList) ? bookingList : []);
      } catch (err) {
        console.error("getPartnerInfo failed:", err);
        if (!cancelled) {
          setPartner(null);
          showToast("Something went wrong while fetching partner details.", "Error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPartner();
    return () => {
      cancelled = true;
    };
  }, [userID, refreshNonce]);

  const activeBookings = useMemo(
    () => bookings.filter(isActiveBooking),
    [bookings]
  );
  const visitBookings = useMemo(() => {
    // Visits = bookings with scheduled duties, else fall back to active bookings
    const withDuties = bookings.filter(hasDuties);
    return withDuties.length ? withDuties : activeBookings;
  }, [bookings, activeBookings]);

  const normalizedSearch = recordSearch.trim().toLowerCase();
  const visibleBookings = useMemo(
    () =>
      activeBookings.filter(
        (visit) =>
          matchesBookingFilter(visit, bookingFilter) &&
          (!normalizedSearch || getSearchText(visit).includes(normalizedSearch))
      ),
    [activeBookings, bookingFilter, normalizedSearch]
  );
  const visibleVisitRows = useMemo(() => {
    const flatList = [];

    visitBookings.forEach((visit) => {
      if (normalizedSearch && !getSearchText(visit).includes(normalizedSearch)) {
        return;
      }

      const duties = [...getDisplayDuties(visit)].sort((a, b) => {
        const va = a?.date ? moment(a.date).valueOf() : 0;
        const vb = b?.date ? moment(b.date).valueOf() : 0;
        return va - vb;
      });

      duties.forEach((duty, dutyIndex) => {
        flatList.push({
          visit,
          duty,
          dutyDayNumber: duty?.day || dutyIndex + 1,
          totalDutyDays: duties.length,
          rowKey:
            normalizeId(duty?._id) ||
            `${normalizeId(visit?._id) || "visit"}-${duty?.date || dutyIndex}`,
        });
      });
    });

    return flatList.filter(({ visit, duty }) => {
      if (["all", "upcoming", "ongoing"].includes(visitFilter)) {
        if (duty?.date) {
          const dutyMoment = moment(duty.date).startOf("day");
          const todayMoment = moment().startOf("day");
          if (dutyMoment.isBefore(todayMoment)) {
            if (
              visitFilter === "all" &&
              matchesVisitDutyFilter(visit, duty, "payment")
            ) {
              return true;
            }
            return false;
          }
        }
      }
      return matchesVisitDutyFilter(visit, duty, visitFilter);
    });
  }, [visitBookings, visitFilter, normalizedSearch]);

  const visitMonthGroups = useMemo(() => {
    const groups = {
      today: [],
      tomorrow: [],
      week: [],
      month: [],
      completed: [],
    };

    visibleVisitRows.forEach((row) => {
      const visitStatus = getVisitStatusKey(row.visit);
      const category = getDutyMonthCategory(row.duty?.date, visitStatus);
      groups[category].push(row);
    });

    const categories =
      visitFilter === "completed"
        ? ["completed"]
        : ["today", "tomorrow", "week", "month"];

    if (visitFilter !== "completed" && groups.completed.length > 0) {
      categories.push("completed");
    }

    return categories
      .map((key) => ({
        key,
        label: VISIT_MONTH_CATEGORY_LABELS[key],
        rows: groups[key],
      }))
      .filter((section) => section.rows.length > 0);
  }, [visibleVisitRows, visitFilter]);

  const selectedDuties = useMemo(() => {
    if (!selectedVisit) return [];
    const arr = getDisplayDuties(selectedVisit);
    return [...arr].sort((a, b) => {
      const va = a?.date ? moment(a.date).valueOf() : 0;
      const vb = b?.date ? moment(b.date).valueOf() : 0;
      return va - vb;
    });
  }, [selectedVisit]);

  const selectedBookingDuties = useMemo(() => {
    if (!selectedBooking) return [];
    return [...getDisplayDuties(selectedBooking)].sort((a, b) => {
      const va = a?.date ? moment(a.date).valueOf() : 0;
      const vb = b?.date ? moment(b.date).valueOf() : 0;
      return va - vb;
    });
  }, [selectedBooking]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[70vh]">
        <Loader loading={loading} />
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="min-h-[100dvh] bg-[#F5F7FB] flex flex-col items-center justify-center px-6 text-center">
        <img
          src={CommonImages.noDuty}
          alt=""
          className="h-[120px] w-[150px] object-contain opacity-70"
        />
        <p className="font-poppins text-[14px] font-semibold text-[#787998] mt-2">
          Partner details unavailable
        </p>
        <button
          type="button"
          onClick={() => navigate("/admin/partnerlistMobile")}
          className="mt-4 text-[13px] font-poppins text-[#1D4ED8] font-medium"
        >
          Back to partners
        </button>
      </div>
    );
  }

  const partnerPhone = partner?.contactInfo?.[0]?.phone;

  const TABS = [
    { key: "careTeam", label: "Team", icon: FiUsers, count: careTeam.length },
    { key: "patients", label: "Patients", icon: FiUser, count: patients.length },
    { key: "bookings", label: "Bookings", icon: FiClipboard, count: activeBookings.length },
    { key: "visits", label: "Visits", icon: FiActivity, count: visitBookings.length },
  ];

  const switchTab = (key) => {
    setActiveTab(key);
    setRecordSearch("");
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("partnerId", userID);
    nextParams.set("tab", key);
    navigate(
      { pathname: location.pathname, search: nextParams.toString() },
      { replace: true, state: { ...location.state, userID } }
    );
    scrollRef.current?.scrollTo({ top: 0 });
  };

  return (
    <div className="h-[100dvh] bg-[#F5F7FB] overflow-hidden">
      <div ref={scrollRef} className="h-full overflow-y-auto overscroll-contain">
        {/* Sticky header + tabs */}
        <div className="sticky top-0 z-20 bg-[#F5F7FB]">
          <div className="flex items-center gap-2 px-4 pt-3 pb-2">
            <button
              type="button"
              onClick={() => navigate("/admin/partnerlistMobile")}
              className="p-2 -ml-2 rounded-full hover:bg-gray-200 transition-colors"
              aria-label="Back"
            >
              <IoMdArrowBack size={22} color="#111827" />
            </button>
            <h2 className="min-w-0 flex-1 text-[18px] font-poppins font-semibold text-[#1F2937] truncate">
              {capitalizeFirstLetter(partner?.basicInfo?.name) || "Partner"}
            </h2>
            <button
              type="button"
              onClick={() => setRefreshNonce((value) => value + 1)}
              className="shrink-0 rounded-full bg-white p-2 text-[#1D4ED8] shadow-sm transition-colors hover:bg-[#EEF2FF]"
              aria-label="Refresh partner details"
              title="Refresh"
            >
              <MdRefresh size={18} />
            </button>
          </div>

          {/* Tab strip */}
          <div className="px-4 pb-2">
            <div className="flex gap-1 bg-white border border-[#E5E7EB] rounded-xl p-1">
              {TABS.map((tab) => {
                const active = activeTab === tab.key;
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => switchTab(tab.key)}
                    className={[
                      "flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-[11px] font-poppins font-medium transition-colors",
                      active ? "bg-[#1D4ED8] text-white" : "text-[#6B7280]",
                    ].join(" ")}
                  >
                    <Icon size={13} />
                    <span>{tab.label}</span>
                    <span
                      className={[
                        "inline-flex items-center justify-center min-w-[16px] h-[15px] px-1 rounded-full text-[9px] font-semibold",
                        active ? "bg-white/20 text-white" : "bg-[#EEF2FF] text-[#1D4ED8]",
                      ].join(" ")}
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Tab content */}
        <div className="px-4 pb-8 space-y-3">
          {/* Summary card */}
          <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-sm p-4 flex items-center gap-3">
            <img
              src={CommonImages.menwithoutactive}
              alt=""
              className="h-12 w-12 rounded-full object-cover shrink-0"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                {partner?.providerType && (
                  <span className="text-[10px] font-poppins font-medium text-[#1D4ED8] bg-[#EEF2FF] px-2 py-0.5 rounded-full capitalize">
                    {partner.providerType}
                  </span>
                )}
                {partner?.addressInfo?.city && (
                  <span className="flex items-center gap-1 text-[11px] font-poppins text-gray-500 min-w-0">
                    <MdLocationOn size={12} className="shrink-0" />
                    <span className="truncate">{capitalizeFirstLetter(partner.addressInfo.city)}</span>
                  </span>
                )}
              </div>
              <div className="mt-1 space-y-0.5">
                {partnerPhone && (
                  <Field icon={MdPhone}>{formatPhoneNumber(Number(partnerPhone))}</Field>
                )}
                {partner?.referralCode && (
                  <p className="text-[11px] font-poppins text-gray-400">
                    Referral: <span className="text-gray-600">{partner.referralCode}</span>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Care Team */}
          {activeTab === "careTeam" && (
            <div className="space-y-2">
              {careTeam.length === 0 ? (
                <SectionEmpty text="No care team members" />
              ) : (
                careTeam.map((s, idx) => {
                  const score = getProfileScore(s);
                  const phone = getSpecialistPhone(s);
                  const email = getSpecialistEmail(s);
                  return (
                    <Card key={s?._id || idx}>
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-poppins font-semibold text-[13px] text-[#1F2937] truncate">
                          {getSpecialistName(s)}
                        </p>
                        {(s?.subRole || s?.role) && (
                          <span className="shrink-0 text-[10px] font-poppins font-medium text-[#1D4ED8] bg-[#EEF2FF] px-2 py-0.5 rounded-full">
                            {getRoleLabel(s?.subRole, s?.role)}
                          </span>
                        )}
                      </div>
                      <div className="mt-2 space-y-1">
                        {phone && <Field icon={MdPhone}>{formatPhoneNumber(Number(phone))}</Field>}
                        {email && <Field icon={MdMail}>{email}</Field>}
                      </div>
                      <div className="mt-2 text-[10px] font-poppins text-gray-400">
                        Profile <span className="font-semibold text-[#1D4ED8]">{score}/2</span>
                      </div>
                    </Card>
                  );
                })
              )}
            </div>
          )}

          {/* Patients */}
          {activeTab === "patients" && (
            <div className="space-y-2">
              {patients.length === 0 ? (
                <SectionEmpty text="No patients" />
              ) : (
                patients.map((s, idx) => {
                  const phone = getSpecialistPhone(s);
                  const email = getSpecialistEmail(s);
                  const dob = getSpecialistDob(s);
                  return (
                    <Card key={s?._id || idx}>
                      <p className="font-poppins font-semibold text-[13px] text-[#1F2937] truncate">
                        {getSpecialistName(s)}
                      </p>
                      <div className="mt-2 space-y-1">
                        {phone && <Field icon={MdPhone}>{formatPhoneNumber(Number(phone))}</Field>}
                        {email && <Field icon={MdMail}>{email}</Field>}
                        {dob && <Field icon={MdCake}>{moment(dob).format("DD MMM YYYY")}</Field>}
                        {s?.createdAt && (
                          <Field>{`Since ${moment(s.createdAt).format("DD MMM YYYY")}`}</Field>
                        )}
                      </div>
                    </Card>
                  );
                })
              )}
            </div>
          )}

          {/* Bookings */}
          {activeTab === "bookings" && (
            <div className="space-y-2">
              <FilterToolbar
                value={recordSearch}
                onChange={setRecordSearch}
                filter={bookingFilter}
                onFilterChange={setBookingFilter}
                options={BOOKING_FILTERS}
              />
              {visibleBookings.length === 0 ? (
                <SectionEmpty text="No bookings match the selected filters" />
              ) : (
                visibleBookings.map((b, idx) => (
                  <button
                    key={b?._id || idx}
                    type="button"
                    onClick={() => setSelectedBooking(b)}
                    className="w-full rounded-xl border border-[#E5E7EB] bg-white p-3.5 text-left shadow-sm transition-colors active:bg-gray-50"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-poppins font-semibold text-[13px] text-[#1F2937] truncate">
                        {getPatientName(b)}
                      </p>
                      <div className="flex shrink-0 items-center gap-1">
                        <StatusBadge status={getBookingStatus(b)} />
                        <FiChevronRight size={16} className="text-gray-300" />
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5">
                      <Field icon={FiUser}>{getServiceRole(b)}</Field>
                      <Field icon={FiCalendar}>{getDateRange(b)}</Field>
                      <Field icon={FiUsers}>{getDutyStaff(b)}</Field>
                      <Field icon={FiClock}>{getDutyType(b)}</Field>
                    </div>
                    <div className="mt-2 flex items-center justify-between border-t border-[#F0F2F5] pt-2">
                      <span className="font-poppins text-[11px] text-gray-500">
                        {capitalizeFirstLetter(getPatientGender(b)) || "—"} · {getPatientAge(b)}
                      </span>
                      <span className="font-poppins text-[11px] text-gray-600 truncate px-2 text-center">
                        {b?.visitInfo?.slot || "—"}
                      </span>
                      <span className="font-poppins text-[11px] font-semibold text-[#1D4ED8]">
                        {b?.visitInfo?.days || 0} {Number(b?.visitInfo?.days) === 1 ? "day" : "days"}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}

          {/* Visits */}
          {activeTab === "visits" && (
            <div className="space-y-2">
              <FilterToolbar
                value={recordSearch}
                onChange={setRecordSearch}
                filter={visitFilter}
                onFilterChange={setVisitFilter}
                options={VISIT_FILTERS}
              />
              {visibleVisitRows.length === 0 ? (
                <SectionEmpty text="No visits match the selected filters" />
              ) : (
                visitMonthGroups.map((section) => (
                  <div key={section.key} className="space-y-2">
                    <div className="flex items-center gap-2 px-1 pt-1">
                      <FiCalendar size={14} className="text-[#1D4ED8] shrink-0" />
                      <p className="font-poppins text-[12px] font-semibold text-gray-700">
                        {section.label} ({section.rows.length})
                      </p>
                    </div>
                    {section.rows.map((row) => {
                      const { visit, duty, dutyDayNumber, totalDutyDays, rowKey } = row;
                      const dutyDateLabel = duty?.displayDateRange
                        ? duty.displayDateRange
                        : duty?.date
                          ? moment(duty.date).format("DD MMM YYYY")
                          : "—";
                      const dutyTime = getDutyTimeLabel(visit, duty);
                      const dutyStatus = getDutyStatusLabel(visit, duty);
                      const staffName = getDutyHcpName(visit, duty);

                      return (
                        <button
                          key={rowKey}
                          type="button"
                          onClick={() => setSelectedVisit(visit)}
                          className="w-full bg-white border border-[#E5E7EB] rounded-xl p-3.5 text-left shadow-sm active:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-poppins font-semibold text-[13px] text-[#1F2937] truncate">
                              {getPatientName(visit)}
                            </p>
                            <div className="flex items-center gap-1 shrink-0">
                              <DutyStatusBadge status={dutyStatus} />
                              <FiChevronRight size={16} className="text-gray-300" />
                            </div>
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                            <Field icon={FiUser}>{getServiceRole(visit)}</Field>
                            <Field icon={FiCalendar}>{dutyDateLabel}</Field>
                          </div>
                          <div className="mt-2 flex items-center justify-between gap-2">
                            <Field icon={FiUsers}>{staffName}</Field>
                            <span className="shrink-0 text-[10px] font-poppins font-semibold text-[#1D4ED8] bg-[#EEF2FF] px-2 py-0.5 rounded-full">
                              {dutyTime && dutyTime !== "-" ? dutyTime : "—"}
                              {totalDutyDays > 1
                                ? ` · Day ${dutyDayNumber}/${totalDutyDays}`
                                : ""}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {selectedBooking && (
        <DutyDetails
          visit={selectedBooking}
          duties={selectedBookingDuties}
          mode="bookings"
          onClose={() => setSelectedBooking(null)}
        />
      )}

      {/* Duty Details overlay (opened by tapping a visit) */}
      {selectedVisit && (
        <DutyDetails
          visit={selectedVisit}
          duties={selectedDuties}
          mode="visits"
          onClose={() => setSelectedVisit(null)}
        />
      )}
    </div>
  );
};

export default PartnerViewMobile;
